/**
 * Entitlements Service
 *
 * Service layer for interacting with the Erebus entitlements API.
 * Provides full CRUD operations for client entitlements management.
 *
 * Both console and frontend apps can use this service:
 * - Console: Uses all methods for admin UI
 * - Frontend: Uses only read methods (getClientModules) for permission checks
 * - Tree-shaking: Unused methods are removed from production builds
 */

import type { AxiosInstance } from 'axios'
import type {
  ModulesResponse,
  ModuleActionRequest,
  PatchTierRequest,
  TierListResponse,
  PaginationParams,
  ClientEntitlements,
  BulkEntitlementsRequest,
  BulkEntitlementsResponse
} from './types'
import type { ModuleValue, TierValue } from './enums'
import { toClientEntitlements } from './types'

export class EntitlementsService {
  private readonly basePath = '/entitlements'

  /**
   * @param axios - Axios instance configured with Erebus base URL and auth interceptors
   */
  constructor(private readonly axios: AxiosInstance) {}

  // =============================================================================
  // READ OPERATIONS (Used by both Console & Frontend)
  // =============================================================================

  /**
   * Get client's complete entitlement data
   *
   * Returns tier, modules, add-ons, and denials for a specific client.
   * Frontend uses this for permission checks and feature flagging.
   *
   * @param clientId - Client ID to fetch entitlements for
   * @returns Complete entitlements data
   *
   * @example
   * const entitlements = await service.getClientEntitlements(123)
   * if (entitlements.modules.includes('in_app_calling')) {
   *   // Show calling UI
   * }
   */
  async getClientEntitlements(clientId: number): Promise<ClientEntitlements> {
    const response = await this.axios.get<ModulesResponse>(
      `${this.basePath}/clients/${clientId}/modules`
    )
    return toClientEntitlements(response.data)
  }

  /**
   * Get client's module list (simple array of module keys)
   *
   * Convenience method for permission checking.
   * Frontend uses this for quick module checks.
   *
   * @param clientId - Client ID to fetch modules for
   * @returns Array of module keys the client has access to
   *
   * @example
   * const modules = await service.getClientModules(123)
   * if (modules.includes('in_app_calling')) {
   *   // Show calling UI
   * }
   */
  async getClientModules(clientId: number): Promise<ModuleValue[]> {
    const entitlements = await this.getClientEntitlements(clientId)
    return entitlements.modules
  }

  /**
   * Check if a client has access to a specific module
   *
   * Convenience method for single module checks.
   *
   * @param clientId - Client ID to check
   * @param module - Module key to check
   * @returns True if client has access to the module
   *
   * @example
   * const hasAccess = await service.hasModule(123, 'in_app_calling')
   */
  async hasModule(clientId: number, module: ModuleValue): Promise<boolean> {
    const modules = await this.getClientModules(clientId)
    return modules.includes(module)
  }

  // =============================================================================
  // ADMIN OPERATIONS (Used by Console only)
  // =============================================================================

  /**
   * Grant a module to a client as an add-on
   *
   * Adds a module outside the client's tier base modules.
   * Requires admin authorization.
   *
   * @param clientId - Client ID to grant module to
   * @param moduleKey - Module key to grant
   * @param reason - Optional reason for the grant
   * @returns Updated entitlements after the grant
   *
   * @example
   * await service.grantModule(123, 'in_app_calling', 'Customer purchased add-on')
   */
  async grantModule(
    clientId: number,
    moduleKey: ModuleValue,
    reason?: string
  ): Promise<ClientEntitlements> {
    const body: ModuleActionRequest = reason ? { reason } : {}
    const response = await this.axios.post<ModulesResponse>(
      `${this.basePath}/clients/${clientId}/modules/${moduleKey}`,
      body
    )
    return toClientEntitlements(response.data)
  }

  /**
   * Deny a module for a client
   *
   * Explicitly removes a module from the client's tier (even if tier includes it).
   * Requires admin authorization.
   *
   * @param clientId - Client ID to deny module for
   * @param moduleKey - Module key to deny
   * @param reason - Optional reason for the denial
   * @returns Updated entitlements after the denial
   *
   * @example
   * await service.denyModule(123, 'in_app_calling', 'Feature causes issues for this client')
   */
  async denyModule(
    clientId: number,
    moduleKey: ModuleValue,
    reason?: string
  ): Promise<ClientEntitlements> {
    const body: ModuleActionRequest = reason ? { reason } : {}
    const response = await this.axios.post<ModulesResponse>(
      `${this.basePath}/clients/${clientId}/modules/${moduleKey}/deny`,
      body
    )
    return toClientEntitlements(response.data)
  }

  /**
   * Revoke a module exception (add-on or denial)
   *
   * Returns client to tier default for this module.
   * Requires admin authorization.
   *
   * @param clientId - Client ID to revoke exception for
   * @param moduleKey - Module key to revoke exception for
   * @returns Updated entitlements after the revocation
   *
   * @example
   * await service.revokeModuleException(123, 'in_app_calling')
   */
  async revokeModuleException(
    clientId: number,
    moduleKey: ModuleValue
  ): Promise<ClientEntitlements> {
    const response = await this.axios.delete<ModulesResponse>(
      `${this.basePath}/clients/${clientId}/modules/${moduleKey}`
    )
    return toClientEntitlements(response.data)
  }

  /**
   * Change a client's tier
   *
   * Updates the client's tier (Core/Premium/Elite).
   * Preserves existing add-ons and denials.
   * Requires admin authorization.
   *
   * @param clientId - Client ID to change tier for
   * @param tier - New tier to assign
   * @returns Updated entitlements after tier change
   *
   * @example
   * await service.changeClientTier(123, 'premium')
   */
  async changeClientTier(clientId: number, tier: TierValue): Promise<ClientEntitlements> {
    const body: PatchTierRequest = { tier }
    const response = await this.axios.patch<ModulesResponse>(
      `${this.basePath}/clients/${clientId}/tier`,
      body
    )
    return toClientEntitlements(response.data)
  }

  /**
   * List all client tiers (paginated)
   *
   * Global admin only endpoint.
   * Returns paginated list of all client tier assignments.
   *
   * @param params - Pagination parameters (limit, offset)
   * @returns Paginated list of client tiers
   *
   * @example
   * const result = await service.listAllClientTiers({ limit: 50, offset: 0 })
   * console.log(`Showing ${result.tiers.length} of ${result.total} clients`)
   */
  async listAllClientTiers(params: PaginationParams = {}): Promise<TierListResponse> {
    const response = await this.axios.get<TierListResponse>(`${this.basePath}/clients/tiers`, {
      params: {
        limit: params.limit || 50,
        offset: params.offset || 0
      }
    })
    return response.data
  }

  /**
   * Get bulk client entitlements (optimized endpoint)
   *
   * Fetches complete entitlement data for multiple clients in a single API call.
   * Much more efficient than calling getClientEntitlements() N times.
   *
   * Global admin only endpoint.
   *
   * @param clientIds - Array of client IDs to fetch entitlements for
   * @returns Map of clientId -> ClientEntitlements
   *
   * @example
   * const clientIds = [123, 456, 789]
   * const bulkData = await service.getBulkClientEntitlements(clientIds)
   * console.log(`Loaded ${Object.keys(bulkData).length} client entitlements`)
   * const client123 = bulkData[123]
   */
  async getBulkClientEntitlements(clientIds: number[]): Promise<Record<number, ClientEntitlements>> {
    const body: BulkEntitlementsRequest = { client_ids: clientIds }
    const response = await this.axios.post<BulkEntitlementsResponse>(
      `${this.basePath}/clients/bulk`,
      body
    )

    // Convert API response to ClientEntitlements format
    const result: Record<number, ClientEntitlements> = {}
    if (response.data.entitlements) {
      for (const [clientIdStr, data] of Object.entries(response.data.entitlements)) {
        const clientId = parseInt(clientIdStr, 10)
        result[clientId] = toClientEntitlements(data)
      }
    }

    return result
  }
}
